import { AppThunk } from "../../app/types"
import { Choice, ChoiceType } from "../simulation-decisions/types"
import { addAlert, incrementAlertId } from "../user-alerts/userAlertsSlice"
import {
  addChoice,
  addEnactedPlan,
  incrementSecondsElapsed,
  removePlan,
  resetSimulation,
  setDuration,
  setSecondsElapsed,
  setTimer,
} from "./simulationSlice"
import { SavePointLogType } from "./types"
import { convertToSeconds, convertToTime } from "./utils"

let activeTimerToken = 0

export const createTimer = (): AppThunk => (dispatch, getState) => {
  if (getState().simulationSlice.timer) {
    console.debug("timer already exists (in thunk)")
    return
  }
  console.debug("creating timer in thunk")
  const timerToken = Date.now()
  activeTimerToken = timerToken
  const timer = setInterval(() => {
    if (activeTimerToken !== timerToken) {
      if (process.env.REACT_APP_TIMER_DEBUG?.toLowerCase() === "true") {
        console.warn("[Simulation] stale timer detected, clearing interval", {
          timerToken,
        })
      }
      clearInterval(timer)
      return
    }
    dispatch(incrementSecondsElapsed())
  }, 1000)
  dispatch(setTimer(timer))
}

export const destroyTimer = (): AppThunk => (dispatch, getState) => {
  const { timer } = getState().simulationSlice
  if (timer) {
    clearInterval(timer)
    dispatch(setTimer(null))
  }
  activeTimerToken = 0
}

export const pauseTimer = (): AppThunk => (dispatch) => {
  dispatch(destroyTimer())
}

export const resumeTimer = (): AppThunk => (dispatch) => {
  dispatch(createTimer())
}

export const resetSimulationSafe = (): AppThunk => (dispatch) => {
  dispatch(destroyTimer())
  dispatch(resetSimulation())
}

export const createTimerWithStartTime =
  (startTime: string): AppThunk =>
  (dispatch) => {
    const secondsElapsed = convertToSeconds(startTime)
    dispatch(setSecondsElapsed(secondsElapsed))
    dispatch(createTimer())
  }

export const createTimerWithSecondsElapsed =
  (secondsElapsed: number): AppThunk =>
  (dispatch) => {
    dispatch(setSecondsElapsed(secondsElapsed))
    dispatch(createTimer())
  }

export const setDurationRelativeToNow =
  (timeToAdd: number): AppThunk =>
  (dispatch, getState) => {
    const { secondsElapsed } = getState().simulationSlice
    const newDuration = convertToTime(secondsElapsed + timeToAdd)
    dispatch(setDuration(newDuration))
  }

const addUserAlertPlanExecuted =
  (plan: Choice, runId: number): AppThunk =>
  (dispatch, getState) => {
    // All alerts created at a given state (message count === x) should have a unique id
    // that falls between messageCount and messageCount + 1. This way, we can sort
    // alerts and messages together by id and get a reliable chronological order.
    const nextMessageId = getState().simulationSlice.messages.length + 1
    dispatch(incrementAlertId())
    const nextUserAlertId = getState().userAlertsSlice.currentId
    const sortId = nextMessageId - (1 - Math.log10(nextUserAlertId))
    if (isNaN(runId)) {
      console.error("runId is not a number")
      return
    }
    const elapsedTimeStr = convertToTime(
      getState().simulationSlice.secondsElapsed,
    )
    const alert = {
      cell: "0",
      eventId: plan.item.id.toString(),
      title: "",
      description: plan.item.actionSentence,
      date: new Date().toString(),
      elapsedTime: elapsedTimeStr,
      response: plan.item.responses?.pop() || "",
      action: 0,
      messageType: "user_alert",
      type: "plan_executed",
      validateResponse: "",
      id: sortId,
      currentDate: new Date().toISOString(),
      categoryId: 0,
      planId: plan.item.id,
    } as SavePointLogType
    dispatch(addAlert({ runId, alert }))
  }

export const enactPlan =
  (plan: Choice, runId: number): AppThunk =>
  (dispatch) => {
    if (plan == null) return
    console.debug("enacting plan", plan)
    dispatch(addEnactedPlan(plan))
    dispatch(removePlan(plan))
    const choice: Choice = {
      item: plan.item,
      time: Date.now(),
      choiceType: ChoiceType.eplan,
      connections: plan.connections,
    }
    dispatch(addUserAlertPlanExecuted(plan, runId))
    dispatch(addChoice(choice))
  }
